import { Base64 } from 'js-base64';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { confetti } from '@shared/utils/helpers';
import ListView from '../../../../base/ListView';
import { toImmutable } from '../../../../libs/immutable-transformer';
import { ajaxErrorHandler, defaultSuccess, getPrinterForDayPeriod, userHasPermission } from '../../../../libs/utils';
import { selectUser } from '../../../../redux/auth/selectors';
import { printerSettings } from '../../../../redux/printers/selectors';
import {
  getAccessVouchers,
  invalidateAccessVoucher,
  redeemAccessVoucher,
  reprintAccessVoucherWristband,
} from '../../../../services/api/accessVouchers';
import { getPeriods } from '../../../../services/api/periods';
import { generateVouchers, resetRegenerateVouchersState, sendVouchers } from '../../../../services/api/registrations';
import { printZPL } from '../../../../services/printer';

class _RegistrationAccessVouchers extends React.Component {
  constructor(props) {
    super(props);

    const parsedFilters = queryString.parse(window.location.search);
    const baseFilters = {
      include:
        'crew-member,crew-member.user,access-voucher-template,access-voucher-template.period,access-voucher-template.profile-group,zones',
      ...parsedFilters,
    };

    this.state = {
      vouchers: [],
      vouchersMeta: {},
      vouchersFilters: toImmutable(baseFilters),
      loading: false,
      periods: [],
    };

    this._load = this._load.bind(this);
    this._generate = this._generate.bind(this);
    this._redeem = this._redeem.bind(this);
    this._invalidate = this._invalidate.bind(this);
    this._printAll = this._printAll.bind(this);
  }

  static propTypes = {
    registration: PropTypes.object,
    printerSettings: PropTypes.object,
    user: PropTypes.object,
  };

  UNSAFE_componentWillMount() {
    this._load();

    getPeriods(this.props.registration.eventId).then((response) => {
      this.setState({
        periods: response.data,
      });
    });
  }

  _load(filters = this.state.vouchersFilters) {
    this.setState({
      loading: true,
    });

    getAccessVouchers(this.props.registration.id, filters)
      .then((response) => {
        this.setState({
          vouchers: response.data,
          vouchersMeta: response.meta,
          vouchersFilters: filters,
          loading: false,
        });
      })
      .catch(ajaxErrorHandler);
  }

  _generate() {
    generateVouchers(this.props.registration.id, {
      priority: false,
      sendVouchers: false,
    })
      .then((response) => {
        defaultSuccess('Vouchers are being generated, please give the server a few seconds.');
      })
      .catch(ajaxErrorHandler);
  }

  _sendAll() {
    sendVouchers(this.props.registration.id, {
      sendAccessVouchers: true,
      sendParkingVouchers: false,
    })
      .then((response) => {
        defaultSuccess('Access vouchers are being sent.');
      })
      .catch(ajaxErrorHandler);
  }

  _redeem(item, twice = false) {
    const day = item.accessVoucherTemplate.day ? item.accessVoucherTemplate.day : 'combi';
    const period = item.accessVoucherTemplate.period;

    getPrinterForDayPeriod(day, period, this.props.printerSettings).then((printer) => {
      redeemAccessVoucher(this.props.registration.id, item.id)
        .then((response) => {
          const zpl = Base64.decode(response.zpl);
          printZPL(printer, zpl);
          if (twice) {
            printZPL(printer, zpl);
          }
          defaultSuccess('Wristband Printed');
          confetti(true);
          this._load();
        })
        .catch(ajaxErrorHandler);
    });
  }

  _reprint(item, twice = false) {
    const day = item.accessVoucherTemplate.day ? item.accessVoucherTemplate.day : 'combi';
    const period = item.accessVoucherTemplate.period;

    getPrinterForDayPeriod(day, period, this.props.printerSettings).then((printer) => {
      reprintAccessVoucherWristband(this.props.registration.id, item.id)
        .then((response) => {
          const zpl = Base64.decode(response.zpl);
          printZPL(printer, zpl);
          if (twice) {
            printZPL(printer, zpl);
          }
          defaultSuccess('Wristband Printed');
          confetti(true);
          this._load();
        })
        .catch(ajaxErrorHandler);
    });
  }

  _fakeRedeem(item) {
    redeemAccessVoucher(this.props.registration.id, item.id)
      .then((response) => {
        defaultSuccess('Wristband Printed');
        this._load();
      })
      .catch(ajaxErrorHandler);
  }

  _fakeReprint(item) {
    reprintAccessVoucherWristband(this.props.registration.id, item.id)
      .then((response) => {
        defaultSuccess('Wristband Printed');
        this._load();
      })
      .catch(ajaxErrorHandler);
  }

  _invalidate(item) {
    invalidateAccessVoucher(this.props.registration.id, item.id)
      .then((response) => {
        defaultSuccess('Voucher Invalidated!');
        this._load();
      })
      .catch(ajaxErrorHandler);
  }

  _printAll() {
    this.state.vouchers.forEach((voucher) => {
      if (!voucher.redeemedAt) {
        this._redeem(voucher);
      }
    });
  }

  render() {
    const { vouchers, vouchersMeta, vouchersFilters, loading, periods } = this.state;

    const listViewConf = {
      dataSet: vouchers,
      dataMeta: vouchersMeta,
      columns: [
        {
          key: 'crewMemberId',
          value: 'Crew Member',
          render: (item) => item.crewMember.user.firstName + ' ' + item.crewMember.user.lastName,
        },
        {
          key: 'period',
          value: 'Period',
          render: (item) => item.accessVoucherTemplate.period.name,
        },
        {
          key: 'day',
          value: 'Day',
          render: (item) => (item.accessVoucherTemplate.day ? item.accessVoucherTemplate.day : 'Combi'),
        },
        {
          key: 'zones',
          value: 'Zones',
          render: (item) =>
            item.zones
              ? item.zones
                  .map((zone) => zone.code)
                  .sort()
                  .join('')
              : '',
        },
        {
          key: 'redeemed',
          value: 'Redeemed',
          render: (item) => (item.redeemedAt ? item.redeemedAt : ''),
        },
      ],
      filters: [
        {
          label: 'Name',
          icon: 'la-search',
          type: 'text',
          key: 'user_name',
        },
        {
          label: 'Period',
          icon: 'la-calendar',
          type: 'select',
          key: 'period_id',
          dataset: [{ key: '', value: 'All' }].concat(
            periods.map((period) => {
              return { key: period.id, value: period.name };
            })
          ),
        },
        {
          label: 'Type',
          icon: 'la-calendar',
          type: 'select',
          key: 'type',
          dataset: [
            { key: 'combi', value: 'Only combi' },
            { key: 'day', value: 'Only day' },
          ],
        },
      ],
      baseFilters: vouchersFilters,
      actions: {
        deleteLink: false,
        editLink: false,
        detailsLink: false,

        deleteFunction: this._handleDelete,
        customLink: (item) => (
          <span>
            <a
              href={item.downloadUrl}
              download
              className="btn btn-outline-brand m-btn btn-sm  m-btn--icon m--margin-left-5"
            >
              <span>
                <i className="la la-cloud-download" />
                <span>PDF</span>
              </span>
            </a>
            {!item.redeemedAt && (
              <span>
                {userHasPermission(this.props.user, ['registrations.accessVouchers.printTwice']) && (
                  <button
                    className="btn btn-outline-metal m-btn btn-sm  m-btn--icon m--margin-left-5"
                    onClick={() => this._redeem(item, true)}
                  >
                    <span>
                      <i className="la la-print" />
                      <span>Print Twice</span>
                    </span>
                  </button>
                )}
                <button
                  className="btn btn-outline-success m-btn btn-sm  m-btn--icon m--margin-left-5"
                  onClick={() => this._redeem(item)}
                >
                  <span>
                    <i className="la la-print" />
                    <span>Print</span>
                  </span>
                </button>
                {userHasPermission(this.props.user, ['registrations.accessVouchers.fakePrint']) && (
                  <button
                    className="btn btn-outline-metal m-btn btn-sm  m-btn--icon m--margin-left-5"
                    onClick={() => this._fakeRedeem(item)}
                  >
                    <span>
                      <i className="la la-print" />
                      <span>Fake Print</span>
                    </span>
                  </button>
                )}
              </span>
            )}
            {item.redeemedAt && (
              <span>
                {userHasPermission(this.props.user, ['registrations.accessVouchers.printTwice']) && (
                  <button
                    className="btn btn-outline-metal m-btn btn-sm  m-btn--icon m--margin-left-5"
                    onClick={() => this._reprint(item, true)}
                  >
                    <span>
                      <i className="la la-print" />
                      <span>RePrint Twice</span>
                    </span>
                  </button>
                )}

                <button
                  className="btn btn-outline-success m-btn btn-sm  m-btn--icon m--margin-left-5"
                  onClick={() => this._reprint(item)}
                >
                  <span>
                    <i className="la la-print" />
                    <span>RePrint</span>
                  </span>
                </button>
                {/*<button*/}
                {/*className="btn btn-outline-metal m-btn btn-sm  m-btn--icon m--margin-left-5"*/}
                {/*onClick={() => this._fakeReprint(item)}*/}
                {/*>*/}
                {/*<span>*/}
                {/*<i className="la la-print" />*/}
                {/*<span>Fake RePrint</span>*/}
                {/*</span>*/}
                {/*</button>*/}
              </span>
            )}
            <button
              className="btn btn-outline-danger m-btn btn-sm  m-btn--icon m--margin-left-5"
              onClick={() => this._invalidate(item)}
            >
              <span>
                <i className="la 	la-ban" />
                <span>Invalidate</span>
              </span>
            </button>
          </span>
        ),
      },
      url: '',
    };

    return (
      <div>
        <div className="row">
          <div className="col-xl-12">
            <div className="m-portlet">
              <div className="m-portlet__head">
                <div className="m-portlet__head-caption">
                  <div className="m-portlet__head-title" />
                </div>

                <div className="m-portlet__head-tools">
                  <form>
                    <ul className="m-portlet__nav m--margin-left-20">
                      {userHasPermission(this.props.user, ['events.events']) && (
                        <li className="m-portlet__nav-item">
                          <button
                            type="button"
                            className={'m-portlet__nav-link btn btn-danger m-btn m-btn--icon'}
                            onClick={() => {
                              resetRegenerateVouchersState(this.props.registration.id)
                                .then(() => defaultSuccess('done'))
                                .catch(ajaxErrorHandler);
                            }}
                          >
                            <span>
                              <i className="la la-refresh" />
                              <span>Reset Queue</span>
                            </span>
                          </button>
                        </li>
                      )}

                      <li className="m-portlet__nav-item">
                        <button
                          type="button"
                          className={
                            'm-portlet__nav-link btn btn-primary m-btn m-btn--icon' +
                            (loading ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                          }
                          onClick={() => this._load()}
                        >
                          <span>
                            <i className="la la-refresh" />
                            <span>Refresh</span>
                          </span>
                        </button>
                      </li>
                      <li className="m-portlet__nav-item">
                        <button
                          type="button"
                          className="m-portlet__nav-link btn btn-primary m-btn m-btn--icon"
                          onClick={this._generate}
                        >
                          <span>
                            <i className="la la-file-pdf-o" />
                            <span>Re-Generate</span>
                          </span>
                        </button>
                      </li>
                      <li className="m-portlet__nav-item">
                        <a
                          href={this.props.registration.downloadAccessVouchersUrl}
                          download
                          data-tip="Download all unredeemed vouchers as PDF"
                          className="m-portlet__nav-link btn btn-primary m-btn m-btn--icon"
                        >
                          <span>
                            <i className="la la-cloud-download" />
                            <span>Download</span>
                          </span>
                        </a>
                      </li>
                      <li className="m-portlet__nav-item">
                        <button
                          type="button"
                          className="m-portlet__nav-link btn btn-primary m-btn m-btn--icon"
                          onClick={() => this._sendAll()}
                          data-tip="Will send access vouchers that weren't redeemed yet"
                        >
                          <span>
                            <i className="la la-envelope" />
                            <span>Send All</span>
                          </span>
                        </button>
                      </li>
                      <li className="m-portlet__nav-item">
                        <button
                          type="button"
                          className="m-portlet__nav-link btn btn-primary m-btn m-btn--icon"
                          onClick={() => this._printAll()}
                          data-tip="Will only print unprinted wristands in the current view"
                        >
                          <span>
                            <i className="la la-print" />
                            <span>Print All</span>
                          </span>
                        </button>
                      </li>
                    </ul>
                  </form>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <ListView config={listViewConf} handleFilterUpdate={this._load} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = function (state) {
  return {
    printerSettings: printerSettings(state),
    user: selectUser(state),
  };
};

const mapDispatchToProps = (dispatch) => ({});

const RegistrationAccessVouchers = connect(mapStateToProps, mapDispatchToProps)(_RegistrationAccessVouchers);

export default RegistrationAccessVouchers;
